/**
 * CallsToolbar — matches LeadsToolbarV2 layout & style.
 *
 * Row 1: DateRangeFilter (right-aligned)
 * Row 2: Agent Select | Outgoing | Incoming | Missed | Not Connected | Unlinked | Reset Filter
 * Row 3: Search  ·  Pagination  (same row, #F8FAFB bg)
 */

import { useState, useRef, useEffect } from 'react';
import {
  Box, Button, Chip, Divider, InputAdornment, TextField,
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  PhoneMissed as PhoneMissedIcon,
  PhoneDisabled as PhoneDisabledIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import Pagination from '../../components/common/Pagination';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import AgentSelect from '../../components/common/AgentSelect';

// ── Per-chip color palette ────────────────────────────────────────────────────
// Each entry: { active: { bg, color, border, hover }, inactive: { border, color, hoverBg, hoverBorder, hoverColor } }

const CHIP_COLORS = {
  outgoing: {
    active:   { bg: '#0a6b3e', color: '#fff', border: '#0a6b3e', hover: '#085c34' },
    inactive: { border: '#6ee7b7', color: '#0a6b3e', hoverBg: '#e5f5ec', hoverBorder: '#0a6b3e' },
  },
  incoming: {
    active:   { bg: '#1976d2', color: '#fff', border: '#1976d2', hover: '#1565c0' },
    inactive: { border: '#90caf9', color: '#1976d2', hoverBg: '#e3f2fd', hoverBorder: '#1976d2' },
  },
  missed: {
    active:   { bg: '#c62828', color: '#fff', border: '#c62828', hover: '#b71c1c' },
    inactive: { border: '#ef9a9a', color: '#c62828', hoverBg: '#ffebee', hoverBorder: '#c62828' },
  },
  not_connected: {
    active:   { bg: '#dc2626', color: '#fff', border: '#dc2626', hover: '#b91c1c' },
    inactive: { border: '#fca5a5', color: '#dc2626', hoverBg: '#fef2f2', hoverBorder: '#dc2626' },
  },
  unlinked: {
    active:   { bg: '#7c3aed', color: '#fff', border: '#7c3aed', hover: '#6d28d9' },
    inactive: { border: '#c4b5fd', color: '#7c3aed', hoverBg: '#f5f3ff', hoverBorder: '#7c3aed' },
  },
};

const chipSx = (type, active) => {
  const c = CHIP_COLORS[type];
  return {
    height: '26px',
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '6px',
    cursor: 'pointer',
    flexShrink: 0,
    '& .MuiChip-icon': { fontSize: '13px', ml: '6px' },
    ...(active
      ? {
          bgcolor: c.active.bg,
          color: c.active.color,
          borderColor: c.active.border,
          '& .MuiChip-icon': { fontSize: '13px', ml: '6px', color: c.active.color },
          '&:hover': { bgcolor: c.active.hover },
        }
      : {
          bgcolor: 'transparent',
          borderColor: c.inactive.border,
          color: c.inactive.color,
          '& .MuiChip-icon': { fontSize: '13px', ml: '6px', color: c.inactive.color },
          '&:hover': {
            bgcolor: c.inactive.hoverBg,
            borderColor: c.inactive.hoverBorder,
          },
        }),
  };
};

const DIRECTION_CHIPS = [
  { key: 'outgoing',  label: 'Outgoing',  colorKey: 'outgoing',      Icon: CallMadeIcon },
  { key: 'incoming',  label: 'Incoming',  colorKey: 'incoming',      Icon: CallReceivedIcon },
  { key: 'missed',    label: 'Missed',    colorKey: 'missed',        Icon: PhoneMissedIcon },
];

// ─────────────────────────────────────────────────────────────────────────────

const CallsToolbar = ({
  searchKeyword = '',
  onSearchChange,
  onResetFilters,
  page = 1,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  totalCount = 0,
  dateRangeState,
  onDateRangeChange,
  filterOptions = {},
  appliedFilters = {},
  onQuickFilterChange,
}) => {
  const [localSearch, setLocalSearch] = useState(searchKeyword);
  const searchTimeoutRef = useRef(null);

  useEffect(() => { setLocalSearch(searchKeyword); }, [searchKeyword]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => onSearchChange?.(value), 500);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      clearTimeout(searchTimeoutRef.current);
      onSearchChange?.(localSearch);
    }
  };

  // ── Filter helpers ──────────────────────────────────────────────────────────

  const handleAgentChange = (agentIds) => {
    const f = { ...appliedFilters };
    if (agentIds?.length > 0) f.agent_ids = agentIds;
    else delete f.agent_ids;
    onQuickFilterChange?.(f);
  };

  const handleDirectionToggle = (direction) => {
    const f = { ...appliedFilters };
    const cur = Array.isArray(f.call_direction) ? f.call_direction : [];
    // Single-select: clear NC and unlinked, set only this direction (or clear if already active)
    delete f.status;
    delete f.unlinked;
    if (cur.length === 1 && cur[0] === direction) {
      delete f.call_direction;
    } else {
      f.call_direction = [direction];
    }
    onQuickFilterChange?.(f);
  };

  const handleNotConnectedToggle = () => {
    const f = { ...appliedFilters };
    const isActive = Array.isArray(f.status) && f.status.includes('NC');
    // Single-select: clear direction and unlinked
    delete f.call_direction;
    delete f.unlinked;
    if (isActive) {
      delete f.status;
    } else {
      f.status = ['NC'];
    }
    onQuickFilterChange?.(f);
  };

  const handleUnlinkedToggle = () => {
    const f = { ...appliedFilters };
    const isActive = Boolean(f.unlinked);
    // Single-select: clear direction and status
    delete f.call_direction;
    delete f.status;
    if (isActive) delete f.unlinked;
    else f.unlinked = true;
    onQuickFilterChange?.(f);
  };

  // ── Active checks ───────────────────────────────────────────────────────────

  const isDirectionActive = (d) =>
    Array.isArray(appliedFilters.call_direction) &&
    appliedFilters.call_direction.length === 1 &&
    appliedFilters.call_direction[0] === d;
  const isNCActive = () =>
    Array.isArray(appliedFilters.status) && appliedFilters.status.includes('NC');
  const isUnlinkedActive = () => Boolean(appliedFilters.unlinked);

  const hasAnyFilter =
    appliedFilters.agent_ids?.length > 0 ||
    appliedFilters.call_direction?.length > 0 ||
    isNCActive() ||
    isUnlinkedActive();

  // ── Agents list ─────────────────────────────────────────────────────────────

  const agents = (filterOptions?.agent_filters || []).map((a) => ({
    agent_id: a.agent_id,
    agent_name: a.agent_name,
    email: a.email || '',
    team_name: a.team_name || '',
  }));

  // ── Pagination ───────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>

      {/* ── Row 1: Date Range ──────────────────────────────────────────────── */}
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
        <DateRangeFilter onChange={onDateRangeChange} initialDateRange={dateRangeState} />
      </Box>

      {/* ── Row 2: Quick Filters ──────────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          pb: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          minWidth: 0,
        }}
      >
        {/* Agent Select */}
        {agents.length > 0 && (
          <Box sx={{ width: 320, flexShrink: 0 }}>
            <AgentSelect
              multiple
              limitTags={1}
              value={Array.isArray(appliedFilters.agent_ids) ? appliedFilters.agent_ids : []}
              onChange={handleAgentChange}
              agents={agents}
              label="Agent"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.72rem',
                  minHeight: '30px',
                  height: '30px',
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  flexWrap: 'nowrap',
                  paddingRight: '52px !important',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0A6B3E',
                    boxShadow: '0 0 0 3px rgba(10,107,62,0.12)',
                  },
                },
                '& .MuiInputBase-input': { fontSize: '0.72rem', padding: '2px 4px !important' },
                '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
              }}
            />
          </Box>
        )}

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', alignSelf: 'stretch', my: 0.25 }} />

        {/* Direction chips */}
        {DIRECTION_CHIPS.map(({ key, label, colorKey, Icon }) => (
          <Chip
            key={key}
            label={label}
            size="small"
            icon={<Icon />}
            variant={isDirectionActive(key) ? 'filled' : 'outlined'}
            onClick={() => handleDirectionToggle(key)}
            sx={chipSx(colorKey, isDirectionActive(key))}
          />
        ))}

        {/* Not Connected */}
        <Chip
          label="Not Connected"
          size="small"
          icon={<PhoneDisabledIcon />}
          variant={isNCActive() ? 'filled' : 'outlined'}
          onClick={handleNotConnectedToggle}
          sx={chipSx('not_connected', isNCActive())}
        />

        {/* Unlinked */}
        <Chip
          label="Unlinked"
          size="small"
          icon={<LinkOffIcon />}
          variant={isUnlinkedActive() ? 'filled' : 'outlined'}
          onClick={handleUnlinkedToggle}
          sx={chipSx('unlinked', isUnlinkedActive())}
        />

        {/* Reset Filter */}
        {hasAnyFilter && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltIcon sx={{ fontSize: 12 }} />}
            onClick={onResetFilters}
            sx={{
              height: 26, px: 1, fontSize: '0.7rem', fontWeight: 600,
              borderRadius: '6px', textTransform: 'none', border: '1px solid #fca5a5',
              color: '#DC2626', bgcolor: '#fff', flexShrink: 0, whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': { mr: 0.4 },
              '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      {/* ── Row 3: Search + Pagination ────────────────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#F8FAFB',
          flexWrap: 'nowrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search by call ID, contact, agent..."
          value={localSearch}
          onChange={handleSearchInput}
          onKeyDown={handleSearchKeyDown}
          sx={{
            flex: '0 1 400px',
            minWidth: 220,
            '& .MuiOutlinedInput-root': { height: '30px', borderRadius: '6px', bgcolor: '#fff' },
            '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0 },
            '& .MuiOutlinedInput-notchedOutline': { top: 0 },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
          }}
          slotProps={{
            input: {
              notched: false,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box sx={{ flexShrink: 0, ml: 'auto' }}>
          <Pagination
            totalCount={totalCount}
            currentPage={page}
            pageSize={rowsPerPage}
            totalPages={totalPages}
            hasNext={page < totalPages && totalCount > 0}
            hasPrevious={page > 1}
            onPageChange={onPageChange}
            onPageSizeChange={onRowsPerPageChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CallsToolbar;
